import { useNavigate, useParams } from "react-router-dom";
import { useState, useEffect } from "react";
import { useAuth } from "@/context/AuthContext";
import { useApi } from "@/lib/useApi";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import { PageTitle, Panel, Loading, Empty, ErrorState, Stat } from "@/components/Common";
import { StatusPill } from "@/components/StatusPill";
import { TxnBadge } from "@/components/StatusPill";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { inr, istDateTime, relTime } from "@/lib/format";
import { ArrowLeft, AlertTriangle, Ban, CheckCircle2, Coins, Plus, Save, Loader2 } from "lucide-react";

export default function Machines() {
  const { id } = useParams();
  if (id) return <MachineDetail id={id} />;
  return <MachineList />;
}

function MachineList() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const base = user.role === "admin" ? "/admin" : "/merchant";
  const { data, loading, error, reload } = useApi(`${base}/machines`);
  const [adding, setAdding] = useState(false);

  return (
    <div>
      <PageTitle title={user.role === "admin" ? "Machines" : "My Machines"}
        subtitle="Connectivity, coin balance and availability across the fleet">
        {user.role === "admin" && (
          <button data-testid="add-machine-btn" onClick={() => setAdding(true)}
            className="inline-flex items-center gap-1.5 rounded-sm bg-white text-black px-3 py-2 text-sm hover:bg-white/90 transition-colors">
            <Plus className="h-4 w-4" /> Add Machine
          </button>
        )}
      </PageTitle>
      {adding && <AddMachineModal onClose={() => setAdding(false)} onDone={() => { setAdding(false); reload(); }} />}
      {loading && !data ? <Loading /> : error ? <ErrorState error={error} onRetry={reload} /> :
        data.length === 0 ? <Empty label="No machines assigned" /> : (
        <>
          {/* desktop table */}
          <div className="hidden md:block">
            <Panel title={`Machines (${data.length})`}>
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="machines-table">
                  <thead>
                    <tr className="text-left text-[11px] uppercase tracking-wider text-muted-foreground border-b border-border">
                      <th className="px-4 py-2 font-medium">Code</th>
                      <th className="px-4 py-2 font-medium">Name / Location</th>
                      <th className="px-4 py-2 font-medium">Group</th>
                      <th className="px-4 py-2 font-medium">Connectivity</th>
                      <th className="px-4 py-2 font-medium text-right">Coins</th>
                      <th className="px-4 py-2 font-medium">Availability</th>
                      <th className="px-4 py-2 font-medium">Last Seen</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.map((m) => (
                      <tr key={m.id} onClick={() => navigate(`/machines/${m.id}`)} data-testid={`machine-row-${m.code}`}
                        className="border-b border-border/60 cursor-pointer hover:bg-[#161616] transition-colors">
                        <td className="px-4 py-2.5 mono">{m.code}</td>
                        <td className="px-4 py-2.5"><div className="flex items-center gap-2">{m.name}{m.machine_type && <span className="text-[9px] uppercase tracking-wider rounded-sm border border-border px-1 py-0.5 text-muted-foreground">{m.machine_type}</span>}</div><div className="text-xs text-muted-foreground">{m.location}</div></td>
                        <td className="px-4 py-2.5 text-muted-foreground">{m.group_name}</td>
                        <td className="px-4 py-2.5"><StatusPill status={m.connectivity} testid={`pill-${m.code}`} /></td>
                        <td className="px-4 py-2.5 mono text-right" style={(m.coin_balance != null && m.coin_balance <= m.reserve_threshold) ? { color: "#F59E0B" } : {}}>{m.coin_balance == null ? "—" : m.coin_balance}</td>
                        <td className="px-4 py-2.5">{m.availability === "blocked"
                          ? <span className="text-[#EF4444] inline-flex items-center gap-1 text-xs"><Ban className="h-3 w-3" />Blocked</span>
                          : <span className="text-muted-foreground inline-flex items-center gap-1 text-xs"><CheckCircle2 className="h-3 w-3" />Available</span>}</td>
                        <td className="px-4 py-2.5 text-xs text-muted-foreground mono">{relTime(m.last_seen)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Panel>
          </div>
          {/* mobile cards */}
          <div className="md:hidden space-y-3">
            {data.map((m) => (
              <div key={m.id} onClick={() => navigate(`/machines/${m.id}`)} data-testid={`machine-card-${m.code}`}
                className="rounded-sm border border-border bg-[#0f0f0f] p-4">
                <div className="flex justify-between items-start">
                  <div><div className="mono font-medium">{m.code}</div><div className="text-xs text-muted-foreground">{m.name}</div></div>
                  <StatusPill status={m.connectivity} />
                </div>
                <div className="flex justify-between mt-3 text-xs text-muted-foreground">
                  <span>Coins: <span className="mono text-foreground">{m.coin_balance == null ? "—" : m.coin_balance}</span></span>
                  <span>{m.group_name}</span>
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function MachineDetail({ id }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const base = user.role === "admin" ? "/admin" : "/merchant";
  const { data: m, loading, error, reload } = useApi(`${base}/machines/${id}`);

  if (loading && !m) return <Loading />;
  if (error) return <ErrorState error={error} onRetry={reload} />;

  return (
    <div>
      <button onClick={() => navigate("/machines")} data-testid="back-btn"
        className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-white transition-colors mb-4">
        <ArrowLeft className="h-4 w-4" /> Machines
      </button>
      <PageTitle title={<span className="mono">{m.code}</span>} subtitle={`${m.name} · ${m.location}`}>
        <StatusPill status={m.connectivity} />
      </PageTitle>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-5">
        <Stat label="Coin Balance" value={m.coin_balance == null ? "—" : m.coin_balance} accent={(m.coin_balance != null && m.coin_balance <= m.reserve_threshold) ? "#F59E0B" : undefined} />
        <Stat label="Reserve Threshold" value={m.reserve_threshold} />
        <Stat label="Availability" value={m.availability === "blocked" ? "Blocked" : "Available"} accent={m.availability === "blocked" ? "#EF4444" : undefined} />
        <Stat label="Group" value={m.group_name} />
      </div>

      {m.alerts.length > 0 && (
        <Panel title="Alerts" className="mb-5">
          <ul className="divide-y divide-border">
            {m.alerts.map((a, i) => (
              <li key={i} className="flex items-center gap-3 px-4 py-3" data-testid={`machine-alert-${i}`}>
                <AlertTriangle className="h-4 w-4" style={{ color: a.severity === "critical" ? "#EF4444" : "#F59E0B" }} />
                <span className="text-sm">{a.message}</span>
              </li>
            ))}
          </ul>
        </Panel>
      )}

      {m.machine_type && <PackagesEditor machineId={id} machineType={m.machine_type} onChange={reload} />}

      <div className="grid lg:grid-cols-2 gap-5">
        <Panel title="Coin Status & Refill History">
          <div className="flex items-center gap-3 px-4 py-3 border-b border-border">
            <Coins className="h-5 w-5 text-[#F59E0B]" />
            <div className="text-sm">Balance <span className="mono">{m.coin_balance}</span> coins · Reserve <span className="mono">{m.reserve_threshold}</span></div>
          </div>
          {m.refills.length === 0 ? <Empty label="No refills recorded" /> : (
            <ul className="divide-y divide-border">
              {m.refills.map((r) => (
                <li key={r.id} className="flex justify-between px-4 py-2.5 text-sm" data-testid={`refill-${r.id}`}>
                  <span className="mono text-[#10B981]">+{r.coins_added}</span>
                  <span className="text-muted-foreground">{r.performed_by}</span>
                  <span className="text-muted-foreground text-xs mono">{istDateTime(r.at)}</span>
                </li>
              ))}
            </ul>
          )}
        </Panel>

        <Panel title="Recent Transactions">
          {m.recent_transactions.length === 0 ? <Empty label="No transactions" /> : (
            <ul className="divide-y divide-border">
              {m.recent_transactions.map((t) => (
                <li key={t.id} className="flex items-center justify-between px-4 py-2.5 text-sm">
                  <span className="mono text-xs">{t.id}</span>
                  <TxnBadge status={t.status} />
                  <span className="mono">{inr(t.amount_paise)}</span>
                </li>
              ))}
            </ul>
          )}
        </Panel>
      </div>
    </div>
  );
}

function PackagesEditor({ machineId, machineType, onChange }) {
  const [rows, setRows] = useState(null);
  const [saving, setSaving] = useState(null);
  const isArcade = machineType === "ARCADE";
  const unitLabel = isArcade ? "Plays" : "Coins";

  const load = async () => {
    try {
      const { data } = await api.get(`/machines/${machineId}/packages`);
      setRows(data.map((p) => ({ ...p, _price: p.price_rupees, _units: p.units })));
    } catch (e) { toast.error(apiError(e)); setRows([]); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [machineId]);

  const set = (pid, k, v) => setRows(rows.map((r) => (r.id === pid ? { ...r, [k]: v } : r)));

  const save = async (p) => {
    const price = Number(p._price), units = Number(p._units);
    if (!(price > 0)) return toast.error("Amount must be greater than 0");
    if (!Number.isInteger(units) || units <= 0) return toast.error(`${unitLabel} must be a positive whole number`);
    setSaving(p.id);
    try {
      await api.put(`/machines/${machineId}/packages/${p.id}`, { price_rupees: price, units, slot: p.slot, enabled: p.enabled });
      toast.success("Package saved — machines pick this up on next package check (~15 min)");
      await load(); onChange && onChange();
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(null); }
  };

  const addRow = async () => {
    const nextSlot = (rows?.reduce((a, r) => Math.max(a, r.slot), 0) || 0) + 1;
    try {
      await api.post(`/machines/${machineId}/packages`, { price_rupees: 1, units: 1, slot: nextSlot, enabled: true });
      toast.success("Package added"); await load(); onChange && onChange();
    } catch (e) { toast.error(apiError(e)); }
  };

  if (rows === null) return <Panel title="Packages" className="mb-5"><div className="px-4 py-6 text-sm text-muted-foreground">Loading packages…</div></Panel>;

  return (
    <Panel title={`Packages · ${machineType}`} className="mb-5"
      right={<button data-testid="add-package-btn" onClick={addRow}
        className="inline-flex items-center gap-1 text-xs rounded-sm border border-border px-2 py-1 hover:bg-[#1c1c1c] transition-colors"><Plus className="h-3 w-3" />Add</button>}>
      {rows.length === 0 ? <Empty label="No packages configured" /> : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="packages-table">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-wider text-muted-foreground border-b border-border">
                <th className="px-4 py-2 font-medium">Slot</th>
                <th className="px-4 py-2 font-medium">Price (₹)</th>
                <th className="px-4 py-2 font-medium">{unitLabel}</th>
                <th className="px-4 py-2 font-medium">Enabled</th>
                <th className="px-4 py-2 font-medium text-right">Action</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((p) => (
                <tr key={p.id} className="border-b border-border/60" data-testid={`package-row-${p.slot}`}>
                  <td className="px-4 py-2 mono">{p.slot}</td>
                  <td className="px-4 py-2"><input type="number" min="0" step="0.01" value={p._price} data-testid={`pkg-price-${p.slot}`}
                    onChange={(e) => set(p.id, "_price", e.target.value)} className="w-24 rounded-sm border border-input bg-[#141414] px-2 py-1 mono outline-none focus:ring-2 focus:ring-white" /></td>
                  <td className="px-4 py-2"><input type="number" min="1" step="1" value={p._units} data-testid={`pkg-units-${p.slot}`}
                    onChange={(e) => set(p.id, "_units", e.target.value)} className="w-20 rounded-sm border border-input bg-[#141414] px-2 py-1 mono outline-none focus:ring-2 focus:ring-white" /></td>
                  <td className="px-4 py-2"><button data-testid={`pkg-toggle-${p.slot}`} onClick={() => set(p.id, "enabled", !p.enabled)}
                    className={`text-xs px-2 py-1 rounded-sm border transition-colors ${p.enabled ? "border-[#10B981]/40 text-[#10B981]" : "border-border text-muted-foreground"}`}>{p.enabled ? "Enabled" : "Disabled"}</button></td>
                  <td className="px-4 py-2 text-right"><button data-testid={`pkg-save-${p.slot}`} disabled={saving === p.id} onClick={() => save(p)}
                    className="inline-flex items-center gap-1 text-xs rounded-sm bg-white text-black px-2.5 py-1 hover:bg-white/90 disabled:opacity-50 transition-colors">{saving === p.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <Save className="h-3 w-3" />}Save</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Panel>
  );
}

function AddMachineModal({ onClose, onDone }) {
  const [f, setF] = useState({ machine_type: "COIN", code: "", name: "", reserve_threshold: 100 });
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!f.code.trim() || !f.name.trim()) return toast.error("Code and name are required");
    setBusy(true);
    try {
      await api.post("/admin/machines/provision", { machine_type: f.machine_type, code: f.code.trim(), name: f.name.trim(), reserve_threshold: Number(f.reserve_threshold) || 0 });
      toast.success("Machine provisioned"); onDone();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="bg-[#0f0f0f] border-border">
        <DialogHeader><DialogTitle className="font-head">Add Machine</DialogTitle><DialogDescription className="sr-only">Provision a new machine</DialogDescription></DialogHeader>
        <div className="space-y-3">
          <div><label className="text-[11px] uppercase tracking-wider text-muted-foreground">Type</label>
            <select data-testid="add-machine-type" value={f.machine_type} onChange={(e) => setF({ ...f, machine_type: e.target.value })}
              className="mt-1 w-full rounded-sm border border-input bg-[#141414] px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-white">
              <option value="COIN">Coin Exchange</option><option value="ARCADE">Arcade Reader</option></select></div>
          <TextField label="Machine ID / Code" testid="add-machine-code" value={f.code} onChange={(v) => setF({ ...f, code: v })} />
          <TextField label="Name" testid="add-machine-name" value={f.name} onChange={(v) => setF({ ...f, name: v })} />
          {f.machine_type === "COIN" && <TextField label="Reserve Threshold" testid="add-machine-reserve" type="number" value={f.reserve_threshold} onChange={(v) => setF({ ...f, reserve_threshold: v })} />}
        </div>
        <DialogFooter>
          <button onClick={onClose} className="rounded-sm border border-border px-4 py-2 text-sm hover:bg-[#1c1c1c] transition-colors">Cancel</button>
          <button data-testid="add-machine-save" disabled={busy} onClick={submit} className="rounded-sm bg-white text-black px-4 py-2 text-sm hover:bg-white/90 disabled:opacity-50 transition-colors">Provision</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function TextField({ label, testid, value, onChange, type = "text" }) {
  return (
    <div>
      <label className="text-[11px] uppercase tracking-wider text-muted-foreground">{label}</label>
      <input data-testid={testid} type={type} value={value} onChange={(e) => onChange(e.target.value)}
        className="mt-1 w-full rounded-sm border border-input bg-[#141414] px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-white" />
    </div>
  );
}
